/**
 * ProductTilePrice — the tile-styled instance of the shared <ProductPrice>.
 *
 * The was/now / range / set-price decision lives in `resolvePriceFromHecProduct`
 * (app/shop/utils/price-display.ts), resolved at the call site from the full
 * product. This component only carries the tile's typography and per-state
 * classes so every tile surface (PLP, search, recs, favorites, cross-sell, ATC
 * flyout) renders identically — and a pricing fix made in the resolver
 * propagates here for free.
 *
 * Source references:
 * - sfcc-website/org_hec/cartridge/templates/default/product/components/pricing/
 * - hec-composable/hec/components/product-tile-cms/price.tsx
 */
import { memo } from "react";

import { cn } from "~/lib/ui";
import { ProductPrice } from "~/shop/components/product-price";
import type { PriceDisplay } from "~/shop/utils/price-display";

interface ProductTilePriceProps {
  /** Resolved via `resolvePriceFromHecProduct(product)` at the call site. */
  display: PriceDisplay | null;
  className?: string;
  /** Per-SKU test id from the tile, e.g. `product-tile-price-${sku}`. No
   *  default — the consumer owns the id so grid tiles never collide. */
  "data-testid"?: string;
}

const ProductTilePriceComponent = ({
  display,
  className,
  "data-testid": dataTestId,
}: ProductTilePriceProps) => (
  <ProductPrice
    display={display}
    as="div"
    className={cn(
      "font-grotesque font-medium text-[hsl(var(--tile-price-color))]",
      className,
    )}
    data-testid={dataTestId}
    strikeClassName="mr-1 font-light text-gray-500"
    salePriceClassName="font-semibold"
  />
);
ProductTilePriceComponent.displayName = "ProductTilePriceComponent";

export const ProductTilePrice = memo(ProductTilePriceComponent);
ProductTilePrice.displayName = "ProductTilePrice";
